import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { useGetGuidelineQuery } from "../redux/Apis/Guideline";

const useCommunityController = (
  externalUserId = null,
  selectedFilter = "",
  search = "",
  page = 1,
  rowsPerPage = 10,
) => {
  const location = useLocation();
  const lang = useSelector((state) => state.language.lang);
  const { id } = useParams();
  const { user } = useSelector((state) => state.authSlice);

  const paramUserId = id;
  const stateUserId = location.state?.userId;

  const userId = externalUserId || stateUserId || paramUserId || null;

  const [discussions, setDiscussions] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const { data, isLoading, isFetching, isSuccess, refetch } =
    useGetGuidelineQuery(
      {
        params: {
          lang,
          page,
          rowsPerPage,
          ...(userId ? { user: userId } : {}),
          ...(selectedFilter
            ? {
                user: user?._id,
                type: selectedFilter,
              }
            : {}),
          ...(search ? { search } : {}),
        },
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  useEffect(() => {
    if (isSuccess) {
      const fetchedPosts = Array.isArray(data?.data) ? data.data : [];

      setDiscussions((prev) => {
        if (page === 1) return fetchedPosts;

        const merged = [...prev];
        const indexById = new Map(
          merged.map((item, index) => [item?._id, index]),
        );

        fetchedPosts.forEach((item) => {
          const existingIndex = indexById.get(item?._id);
          if (existingIndex === undefined) {
            indexById.set(item?._id, merged.length);
            merged.push(item);
            return;
          }

          merged[existingIndex] = item;
        });

        return merged;
      });

      const pagination = data?.pagination || data?.meta || data?.pageInfo || {};
      const total =
        pagination.total ?? pagination.totalCount ?? pagination.count;
      const currentPage = pagination.page ?? page;
      const currentRowsPerPage =
        pagination.rowsPerPage ?? pagination.limit ?? rowsPerPage;

      let nextHasMore = false;

      if (typeof pagination.hasNext === "boolean") {
        nextHasMore = pagination.hasNext;
      } else if (typeof total === "number") {
        nextHasMore = currentPage * currentRowsPerPage < total;
      } else {
        nextHasMore = fetchedPosts.length === rowsPerPage;
      }

      setHasMore(nextHasMore);
    }
  }, [isSuccess, data, page, rowsPerPage]);

  return {
    values: {
      user,
      data: discussions,
      isLoading: page === 1 ? isLoading || isFetching : false,
      isLoadingMore: page > 1 && isFetching,
      hasMore,
    },
    functions: {
      setDiscussions,
    },
    refetch,
  };
};

export default useCommunityController;
